import {
  Badge,
  cn,
  CopyButton,
  EmptyState,
  Glass,
  Spinner,
  toast,
} from "@gotts.ai/ui";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { formatEther } from "viem";

import { AccountIdenticon } from "../components/AccountIdenticon";
import { AnimatedBalance } from "../components/AnimatedBalance";
import { ProtocolBadge } from "../components/ProtocolBadge";
import { TokenIcon } from "../components/TokenIcon";
import { useDeployment, useEvmBackend } from "../context";
import { useAddressResolver } from "../hooks/useAddressResolver";
import { useBlockNumber } from "../hooks/useBlockNumber";
import { useRpcRequest } from "../hooks/useRpcRequest";
import { formatEth, formatGasCompact, formatRelativeTime } from "../lib/format";
import {
  decodeCalldata,
  decodeTx,
  formatLogArgWithContext,
  tryDecodeLogs,
} from "../lib/tx-decoder";
import { ACTION_CATEGORY_COLORS, classifyAction } from "../lib/tx-signatures";

import type { DecodedLog, DecodedTx } from "../lib/tx-decoder";
import type { Address, Hex } from "viem";

interface TxReceipt {
  gasUsed: string;
  logs: { topics: Hex[]; data: Hex; address?: string }[];
  status: string;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TxEntry {
  blockNumber: number;
  timestamp: number;
  hash: string;
  from: string;
  to: string | null;
  value: string;
  input: Hex;
  decoded: DecodedTx;
  gasUsed?: number;
  status?: "success" | "reverted";
}

// ---------------------------------------------------------------------------
// Well-known account name aliases
// ---------------------------------------------------------------------------

const LABEL_ALIASES: Record<string, string> = {
  Deployer: "Deployer",
  "Account 1": "Alice",
  "Account 2": "Bob",
  "Account 3": "Carol",
  "Account 4": "Dave",
  "Account 5": "Eve",
};

function getAccountName(
  address: string,
  names: Record<Address, string>,
): string | null {
  for (const [addr, name] of Object.entries(names)) {
    if (addr.toLowerCase() === address.toLowerCase()) return name;
  }
  return null;
}

/** Format ETH value from hex, returning em-dash for zero */
function formatEthDisplay(hexValue: string): string {
  const wei = BigInt(hexValue);
  if (wei === 0n) return "\u2014";
  const eth = parseFloat(formatEther(wei));
  if (eth < 0.0001) return "<0.0001";
  return formatEth(eth);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimestamp(ts: number): string {
  const d = new Date(ts * 1000);
  return d.toLocaleTimeString("en-US", { hour12: false });
}

// ---------------------------------------------------------------------------
// Protocol color mapping for left borders
// ---------------------------------------------------------------------------

const PROTOCOL_ACCENT: Record<string, string> = {
  V2: "#4ade80",
  V3: "#60a5fa",
  V4: "#a855f7",
  ERC20: "#fbbf24",
  Vault: "#10b981",
};

const PROTOCOL_GAS_COLORS: Record<string, string> = {
  V2: "bg-green-500",
  V3: "bg-blue-500",
  V4: "bg-purple-500",
  ERC20: "bg-amber-500",
  Vault: "bg-emerald-500",
};

// ---------------------------------------------------------------------------
// Event icons (Unicode only per project rules)
// ---------------------------------------------------------------------------

const EVENT_ICONS: Record<string, string> = {
  Transfer: "\u2194",
  Deposit: "\u2193",
  Withdrawal: "\u2191",
  Approval: "\u2713",
  Swap: "\u21C4",
  Mint: "+",
  Burn: "\u2212",
  VaultCreated: "\u2726",
  FeeReport: "\u00A7",
};

// ---------------------------------------------------------------------------
// Event name color mapping
// ---------------------------------------------------------------------------

function eventColorClass(eventName: string): string {
  const lower = eventName.toLowerCase();
  if (lower === "transfer") return "text-blue-400";
  if (lower === "approval") return "text-amber-400";
  if (lower === "deposit" || lower === "mint") return "text-green-400";
  if (lower.includes("swap")) return "text-purple-400";
  return "text-gold";
}

/** Contextualize "Zero Address" in event args based on arg name */
function contextualizeZeroAddr(argName: string, formatted: string): string {
  if (formatted !== "Zero Address") return formatted;
  const lower = argName.toLowerCase();
  if (lower === "from" || lower === "src" || lower === "sender")
    return "Zero Address (Mint)";
  if (lower === "to" || lower === "dst" || lower === "recipient")
    return "Zero Address (Burn)";
  return "Zero Address";
}

// ---------------------------------------------------------------------------
// Filter tabs
// ---------------------------------------------------------------------------

type ProtocolFilter = "All" | "V2" | "V3" | "V4" | "ERC20" | "Vault" | "Other";

const PROTOCOL_FILTERS: ProtocolFilter[] = [
  "All",
  "V2",
  "V3",
  "V4",
  "ERC20",
  "Vault",
  "Other",
];

function matchesFilter(tx: TxEntry, filter: ProtocolFilter): boolean {
  if (filter === "All") return true;
  if (filter === "Other") {
    return (
      tx.decoded.protocol === "Unknown" || tx.decoded.protocol === "ERC-8004"
    );
  }
  return tx.decoded.protocol === filter;
}

function protocolForGas(protocol: string): string {
  if (protocol === "Vault") return "Vault";
  if (protocol === "ERC-8004") return "Other";
  if (protocol === "Unknown") return "Other";
  return protocol;
}

// ---------------------------------------------------------------------------
// Known token symbols (for TokenIcon in TO column)
// ---------------------------------------------------------------------------

const KNOWN_TOKEN_SYMBOLS = new Set([
  "WETH",
  "WETH9",
  "USDC",
  "USDT",
  "DAI",
  "WBTC",
  "ETH",
  "UNI",
]);

// ---------------------------------------------------------------------------
// Resolved address cell component
// ---------------------------------------------------------------------------

function ResolvedAddressCell({
  address,
  names,
  onClickAddress,
  dimRepeated,
}: {
  address: string;
  names: Record<Address, string>;
  onClickAddress: (addr: string) => void;
  dimRepeated?: boolean;
}) {
  const label = getAccountName(address, names);
  const isToken = label ? KNOWN_TOKEN_SYMBOLS.has(label) : false;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClickAddress(address);
      }}
      className={cn(
        "flex items-center gap-1 font-mono text-[11px] transition-colors hover:text-[var(--color-gold)]",
        dimRepeated && "opacity-30",
      )}
      title={address}
    >
      {isToken ? (
        <TokenIcon symbol={label ?? ""} size={20} />
      ) : (
        <AccountIdenticon address={address} size={20} />
      )}
      <span
        className={cn("truncate", label ? "text-text-dim" : "text-text-muted")}
      >
        {label ?? `${address.slice(0, 6)}\u2026${address.slice(-4)}`}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Inline copy button wrapper
// ---------------------------------------------------------------------------

function CopyBtn({ text, className }: { text: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <CopyButton
      copied={copied}
      onClick={() => {
        void navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      }}
      className={className}
    />
  );
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_TX = 50;

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function TransactionLog() {
  const { data: blockNumber } = useBlockNumber();
  const { request } = useRpcRequest();
  const { deployment } = useDeployment();
  const { accounts } = useEvmBackend();
  const { labelMap } = useAddressResolver();

  const [transactions, setTransactions] = useState<TxEntry[]>([]);
  const [_lastFetchedBlock, setLastFetchedBlock] = useState<number>(0);
  const lastFetchedBlockRef = useRef(0);
  const [protocolFilter, setProtocolFilter] = useState<ProtocolFilter>("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedTx, setExpandedTx] = useState<string | null>(null);
  const [expandedReceipts, setExpandedReceipts] = useState<
    Record<string, TxReceipt>
  >({});
  const [expandedDecodedLogs, setExpandedDecodedLogs] = useState<
    Record<string, DecodedLog[]>
  >({});
  const scrollRef = useRef<HTMLDivElement>(null);
  const [txLimit, setTxLimit] = useState(MAX_TX);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isSyncingBlocks, setIsSyncingBlocks] = useState(false);
  const [syncRange, setSyncRange] = useState<{
    from: number;
    to: number;
  } | null>(null);

  // Merge backend account names + deployed contracts + token addresses
  const mergedNames = useMemo(() => {
    const names: Record<string, string> = { ...labelMap };
    for (const acct of accounts) {
      names[acct.address.toLowerCase()] =
        LABEL_ALIASES[acct.label] ?? acct.label;
    }
    if (deployment?.tokens) {
      for (const [sym, addr] of Object.entries(deployment.tokens)) {
        names[(addr as string).toLowerCase()] = sym.toUpperCase();
      }
    }
    if (deployment?.vault) {
      const vaultNames: Record<string, string> = {
        identityAdapter: "IdentityAdapter",
        feeModule: "FeeModule",
        vaultImplementation: "VaultImpl",
        vaultFactory: "VaultFactory",
      };
      for (const [key, addr] of Object.entries(deployment.vault)) {
        names[(addr as string).toLowerCase()] =
          vaultNames[key] ?? `Vault:${key}`;
      }
    }
    return names as Record<Address, string>;
  }, [accounts, deployment?.tokens, deployment?.vault, labelMap]);

  // Click an address in a table row to populate search
  const handleAddressClick = useCallback((address: string) => {
    setSearchQuery(address);
  }, []);

  // Fetch receipt when a tx is expanded
  const handleExpandTx = useCallback(
    async (txHash: string) => {
      if (expandedTx === txHash) {
        setExpandedTx(null);
        return;
      }
      setExpandedTx(txHash);
      if (expandedReceipts[txHash]) return; // already fetched
      try {
        const receipt = await request("eth_getTransactionReceipt", [txHash]);
        if (receipt) {
          const typedReceipt = receipt as TxReceipt;
          setExpandedReceipts((prev) => ({
            ...prev,
            [txHash]: typedReceipt,
          }));
          const decoded = tryDecodeLogs(typedReceipt.logs);
          setExpandedDecodedLogs((prev) => ({ ...prev, [txHash]: decoded }));
        }
      } catch {
        // Silently ignore receipt fetch failures
      }
    },
    [expandedTx, expandedReceipts, request],
  );

  useEffect(() => {
    if (!blockNumber) return;

    const bn = Number(blockNumber);
    if (bn <= lastFetchedBlockRef.current) return;

    // Set ref immediately to prevent duplicate fetches across renders
    const deployBlock = deployment?.blockNumber ?? 0;
    const startBlock =
      lastFetchedBlockRef.current === 0
        ? Math.max(1, deployBlock > 0 ? deployBlock : bn - 5)
        : lastFetchedBlockRef.current + 1;

    setIsSyncingBlocks(true);
    setSyncRange({ from: startBlock, to: bn });
    lastFetchedBlockRef.current = bn;

    const limit = txLimit;

    async function fetchBlocks() {
      const newTxs: TxEntry[] = [];

      try {
        for (let b = startBlock; b <= bn; b++) {
          try {
            const block = (await request("eth_getBlockByNumber", [
              "0x" + b.toString(16),
              true,
            ])) as Record<string, unknown> | null;
            if (block?.transactions) {
              const typedBlock = block as {
                timestamp?: string;
                transactions: {
                  input?: string;
                  to?: string;
                  hash: string;
                  from: string;
                  value?: string;
                }[];
              };
              const blockTimestamp = Number(
                BigInt(typedBlock.timestamp ?? "0x0"),
              );
              for (const tx of typedBlock.transactions) {
                const input = (tx.input ?? "0x") as Hex;
                const to = (tx.to ??
                  "0x0000000000000000000000000000000000000000") as Address;
                const decoded = decodeTx(input, to, mergedNames);

                // Fetch receipt for gas usage + status
                let gasUsed: number | undefined;
                let status: "success" | "reverted" | undefined;
                try {
                  const receipt = (await request("eth_getTransactionReceipt", [
                    tx.hash,
                  ])) as TxReceipt | null;
                  if (receipt?.gasUsed) {
                    gasUsed = Number(BigInt(receipt.gasUsed));
                  }
                  if (receipt?.status) {
                    status = receipt.status === "0x1" ? "success" : "reverted";
                  }
                } catch {
                  // Ignore receipt fetch failures
                }

                newTxs.push({
                  blockNumber: b,
                  timestamp: blockTimestamp,
                  hash: tx.hash,
                  from: tx.from,
                  to: tx.to ?? null,
                  value: tx.value ?? "0x0",
                  input,
                  decoded,
                  gasUsed,
                  status,
                });
              }
            }
          } catch {
            // Skip blocks that fail to fetch
          }
        }

        if (newTxs.length > 0) {
          setTransactions((prev) => {
            const seen = new Set(prev.map((t) => t.hash));
            const unique = newTxs.filter((t) => !seen.has(t.hash));
            return [...prev, ...unique].slice(-limit);
          });
        }

        setLastFetchedBlock(bn);
      } finally {
        setIsInitialLoad(false);
        setIsSyncingBlocks(false);
        setSyncRange(null);
      }
    }

    void fetchBlocks();
  }, [blockNumber, request, mergedNames, txLimit, deployment?.blockNumber]);

  // Auto-scroll to top when new transactions arrive (newest first)
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [transactions]);

  // -----------------------------------------------------------------------
  // Filtered + searched transactions
  // -----------------------------------------------------------------------

  const filteredTxs = useMemo(() => {
    let result = transactions;

    // Protocol filter
    if (protocolFilter !== "All") {
      result = result.filter((tx) => matchesFilter(tx, protocolFilter));
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((tx) => {
        if (tx.hash.toLowerCase().includes(q)) return true;
        if (tx.from.toLowerCase().includes(q)) return true;
        if (getAccountName(tx.from, mergedNames)?.toLowerCase().includes(q))
          return true;
        if (tx.decoded.action.toLowerCase().includes(q)) return true;
        if (tx.decoded.details.toLowerCase().includes(q)) return true;
        if (tx.to?.toLowerCase().includes(q)) return true;
        if (
          tx.to &&
          getAccountName(tx.to, mergedNames)?.toLowerCase().includes(q)
        )
          return true;
        return false;
      });
    }

    // Most recent first
    return result.slice().reverse();
  }, [transactions, protocolFilter, searchQuery, mergedNames]);

  // Compute counts per protocol filter (from unfiltered transactions)
  const filterCounts = useMemo(() => {
    const counts: Record<ProtocolFilter, number> = {
      All: transactions.length,
      V2: 0,
      V3: 0,
      V4: 0,
      ERC20: 0,
      Vault: 0,
      Other: 0,
    };
    for (const tx of transactions) {
      if (matchesFilter(tx, "V2")) counts.V2++;
      else if (matchesFilter(tx, "V3")) counts.V3++;
      else if (matchesFilter(tx, "V4")) counts.V4++;
      else if (matchesFilter(tx, "ERC20")) counts.ERC20++;
      else if (matchesFilter(tx, "Vault")) counts.Vault++;
      else counts.Other++;
    }
    return counts;
  }, [transactions]);

  // Gas summary stats
  const gasStats = useMemo(() => {
    const withGas = transactions.filter((tx) => tx.gasUsed !== undefined);
    if (withGas.length === 0) return null;
    const total = withGas.reduce((sum, tx) => sum + (tx.gasUsed ?? 0), 0);
    const avg = Math.round(total / withGas.length);
    return { total, avg };
  }, [transactions]);

  // Total ETH value across all transactions
  const totalEthValue = useMemo(() => {
    let total = 0n;
    for (const tx of transactions) {
      total += BigInt(tx.value);
    }
    return total;
  }, [transactions]);

  // Gas distribution per protocol (for mini-bar)
  const gasDistribution = useMemo(() => {
    const dist: Record<string, number> = {
      V2: 0,
      V3: 0,
      V4: 0,
      ERC20: 0,
      Vault: 0,
      Other: 0,
    };
    let total = 0;
    for (const tx of transactions) {
      const gas = tx.gasUsed ?? 0;
      total += gas;
      const proto = protocolForGas(tx.decoded.protocol);
      dist[proto] = (dist[proto] ?? 0) + gas;
    }
    if (total === 0) return null;
    return Object.entries(dist)
      .filter(([, v]) => v > 0)
      .map(([proto, gas]) => ({
        protocol: proto,
        percent: (gas / total) * 100,
        color: PROTOCOL_GAS_COLORS[proto] ?? "bg-gray-500",
      }));
  }, [transactions]);

  // Check if all visible transactions share the same sender
  const uniformFrom = useMemo(() => {
    const firstTx = filteredTxs[0];
    if (!firstTx) return null;
    const first = firstTx.from.toLowerCase();
    if (filteredTxs.every((tx) => tx.from.toLowerCase() === first)) {
      return (
        getAccountName(firstTx.from, mergedNames) ??
        `${firstTx.from.slice(0, 6)}\u2026${firstTx.from.slice(-4)}`
      );
    }
    return null;
  }, [filteredTxs, mergedNames]);

  const isFiltered = protocolFilter !== "All" || searchQuery.trim() !== "";
  const isBootstrapping = isInitialLoad && transactions.length === 0;
  const shouldShowSyncBanner = isSyncingBlocks || isBootstrapping;
  const emptyMessage = isFiltered
    ? "No transactions match your current filters. Try clearing filters or broadening search."
    : "No transactions yet. Mine blocks or submit transactions to see them here.";

  const clearFilters = useCallback(() => {
    setProtocolFilter("All");
    setSearchQuery("");
  }, []);

  // -----------------------------------------------------------------------
  // Export handlers
  // -----------------------------------------------------------------------

  const exportCsv = useCallback(() => {
    const headers = [
      "Block",
      "Hash",
      "From",
      "To",
      "Action",
      "Details",
      "Value (ETH)",
      "Gas",
      "Status",
    ];
    const rows = filteredTxs.map((tx) => [
      tx.blockNumber,
      tx.hash,
      getAccountName(tx.from, mergedNames) ?? tx.from,
      tx.to ? (getAccountName(tx.to, mergedNames) ?? tx.to) : "CREATE",
      tx.decoded.action,
      tx.decoded.details,
      tx.value !== "0x0"
        ? parseFloat(formatEther(BigInt(tx.value))).toString()
        : "0",
      tx.gasUsed?.toString() ?? "",
      tx.status ?? "",
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `txlog-${String(Date.now())}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${String(filteredTxs.length)} transactions`);
  }, [filteredTxs, mergedNames]);

  const exportJson = useCallback(() => {
    const data = filteredTxs.map((tx) => ({
      block: tx.blockNumber,
      hash: tx.hash,
      from: getAccountName(tx.from, mergedNames) ?? tx.from,
      to: tx.to ? (getAccountName(tx.to, mergedNames) ?? tx.to) : null,
      action: tx.decoded.action,
      details: tx.decoded.details,
      protocol: tx.decoded.protocol,
      value: tx.value !== "0x0" ? formatEther(BigInt(tx.value)) : "0",
      gas: tx.gasUsed ?? null,
      status: tx.status ?? null,
    }));
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `txlog-${String(Date.now())}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${String(filteredTxs.length)} transactions`);
  }, [filteredTxs, mergedNames]);

  const totalGasFormatted = gasStats
    ? formatGasCompact(gasStats.total)
    : "\u2014";
  const avgGasFormatted = gasStats ? formatGasCompact(gasStats.avg) : "\u2014";
  const ethValueFormatted =
    totalEthValue > 0n
      ? `${formatEth(parseFloat(formatEther(totalEthValue)))} ETH`
      : "0 ETH";

  return (
    <div className="animate-page-in">
      {shouldShowSyncBanner && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-[rgba(201,168,76,0.2)] bg-[rgba(201,168,76,0.06)] px-3 py-2 font-mono text-[11px] text-[var(--color-gold)]">
          <Spinner size={12} />
          <span>
            {isBootstrapping
              ? "Loading transaction history"
              : "Syncing latest transaction activity"}
          </span>
          {syncRange && (
            <span className="text-[10px] text-[var(--color-gold-dim)]">
              blocks {syncRange.from.toLocaleString()}-
              {syncRange.to.toLocaleString()}
            </span>
          )}
        </div>
      )}

      {/* Summary stat cards */}
      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Glass className="glass-card px-4 py-3">
          <div className="hero-stat-label">Transactions</div>
          <div className="mt-1 font-serif text-xl text-[var(--color-text)]">
            {isBootstrapping ? (
              <div className="skeleton h-7 w-20" />
            ) : (
              <AnimatedBalance
                value={filteredTxs.length}
                integer
                emDash={false}
              />
            )}
          </div>
          {isFiltered && (
            <div className="text-text-muted mt-0.5 font-mono text-[10px]">
              of {transactions.length} total
            </div>
          )}
        </Glass>
        <Glass className="glass-card px-4 py-3">
          <div className="hero-stat-label">Gas Used</div>
          <div className="mt-1 font-serif text-xl text-[var(--color-text)]">
            {isBootstrapping ? (
              <div className="skeleton h-7 w-24" />
            ) : (
              totalGasFormatted
            )}
          </div>
          <div className="text-text-muted mt-0.5 font-mono text-[10px]">
            {isBootstrapping ? "indexing" : `avg ${avgGasFormatted}`}
          </div>
        </Glass>
        <Glass className="glass-card px-4 py-3">
          <div className="hero-stat-label">ETH Value</div>
          <div
            className={cn(
              "mt-1 font-serif text-xl",
              totalEthValue > 0n
                ? "text-[var(--color-success)]"
                : "text-[var(--color-text)]",
            )}
          >
            {isBootstrapping ? (
              <div className="skeleton h-7 w-28" />
            ) : (
              ethValueFormatted
            )}
          </div>
        </Glass>
      </div>

      {/* Gas distribution bar */}
      {gasDistribution && (
        <div className="mb-5">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="hero-stat-label">Gas by protocol</span>
            <div className="flex items-center gap-3">
              {gasDistribution.map((seg) => (
                <span
                  key={seg.protocol}
                  className="flex items-center gap-1 font-mono text-[10px] text-[var(--color-text-muted)]"
                >
                  <span
                    className={cn(
                      "inline-block h-2 w-2 rounded-full",
                      seg.color,
                    )}
                  />
                  {seg.protocol}{" "}
                  <span className="text-text-dim">
                    {seg.percent.toFixed(0)}%
                  </span>
                </span>
              ))}
            </div>
          </div>
          <div className="flex h-2 w-full overflow-hidden rounded-full bg-[rgba(255,255,255,0.04)]">
            {gasDistribution.map((seg) => (
              <div
                key={seg.protocol}
                className={cn("h-full transition-all duration-500", seg.color)}
                style={{ width: `${String(seg.percent)}%` }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Filter tabs + search + export */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {PROTOCOL_FILTERS.map((f) => {
          const count = filterCounts[f];
          const isZero = f !== "All" && count === 0;
          return (
            <button
              key={f}
              type="button"
              onClick={() => {
                if (!isZero) setProtocolFilter(f);
              }}
              data-testid={`filter-${f}`}
              className={cn(
                "rounded-full px-3.5 py-1.5 font-mono text-[10px] transition-all duration-200",
                protocolFilter === f
                  ? "bg-[var(--color-gold)] text-black shadow-[0_0_12px_rgba(201,168,76,0.2)]"
                  : "text-text-muted hover:text-gold border border-[var(--glass-border)] bg-transparent hover:border-[rgba(201,168,76,0.3)]",
                isZero && "pointer-events-none opacity-25",
              )}
            >
              {f} (<AnimatedBalance value={count} integer emDash={false} />)
            </button>
          );
        })}

        {isFiltered && (
          <button
            type="button"
            onClick={clearFilters}
            data-testid="clear-filters"
            className="btn-ghost h-auto px-2.5 py-1.5"
          >
            Clear filters
          </button>
        )}

        {uniformFrom && (
          <span className="text-text-muted text-xs">
            All from: <span className="text-text-dim">{uniformFrom}</span>
          </span>
        )}

        <div className="flex-1" />

        <button
          type="button"
          onClick={exportCsv}
          data-testid="export-csv"
          className="chip-preset"
        >
          Export CSV
        </button>
        <button
          type="button"
          onClick={exportJson}
          data-testid="export-json"
          className="chip-preset"
        >
          Export JSON
        </button>

        <div className="relative">
          <svg
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search transactions..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            data-testid="tx-search"
            className="text-text-dim placeholder-text-muted w-full rounded-full border border-[var(--glass-border)] bg-[rgba(255,255,255,0.03)] py-1.5 pl-8 pr-3 font-mono text-xs outline-none transition-colors focus:border-[var(--color-gold)] sm:w-56"
          />
        </div>
      </div>

      {/* Transaction feed */}
      <Glass className="p-0">
        <div
          ref={scrollRef}
          className="max-h-[calc(100vh-420px)] min-h-[200px] overflow-y-auto transition-all duration-300"
        >
          {isBootstrapping ? (
            <div className="space-y-2 p-3">
              {Array.from({ length: 8 }, (_, i) => i).map((i) => (
                <div
                  key={i}
                  className="skeleton skeleton-row rounded"
                  style={{ animationDelay: `${String(i * 80)}ms` }}
                />
              ))}
            </div>
          ) : filteredTxs.length === 0 ? (
            <div className="py-16">
              <EmptyState message={emptyMessage} />
            </div>
          ) : (
            filteredTxs.map((tx, i) => {
              const isExpanded = expandedTx === tx.hash;
              const isReverted = tx.status === "reverted";
              const ethValue = BigInt(tx.value);
              const gasLimit = 30_000_000;
              const gasPercent =
                tx.gasUsed !== undefined ? (tx.gasUsed / gasLimit) * 100 : null;

              const prevTx = filteredTxs[i - 1];
              const isNewBlock =
                i > 0 &&
                prevTx !== undefined &&
                tx.blockNumber !== prevTx.blockNumber;
              const relTime = formatRelativeTime(tx.timestamp * 1000).replace(
                " ago",
                "",
              );

              const protocolColor =
                PROTOCOL_ACCENT[tx.decoded.protocol] ??
                "rgba(255,255,255,0.15)";

              return (
                <Fragment key={`${tx.hash}-${String(i)}`}>
                  {/* Row */}
                  <div
                    onClick={() => handleExpandTx(tx.hash)}
                    className={cn(
                      "group relative cursor-pointer border-b border-[var(--glass-border)] transition-all duration-200",
                      "hover:-translate-y-[1px] hover:shadow-[0_8px_20px_rgba(0,0,0,0.15)]",
                      "hover:bg-[rgba(201,168,76,0.05)]",
                      isExpanded && "bg-[rgba(201,168,76,0.04)]",
                      isReverted && "bg-[rgba(239,68,68,0.04)]",
                      i % 2 === 0 && "bg-[rgba(255,255,255,0.01)]",
                      isNewBlock &&
                        "border-t border-t-[rgba(255,255,255,0.06)]",
                    )}
                  >
                    {/* Protocol accent strip */}
                    <div
                      className="absolute left-0 top-0 h-full w-[3px]"
                      style={{ background: protocolColor }}
                    />

                    {/* Single-row layout */}
                    <div className="flex min-h-[44px] flex-wrap items-center gap-x-3 gap-y-1 py-2 pl-4 pr-3 sm:flex-nowrap">
                      {/* Block / Time (fixed width) */}
                      <div className="hidden shrink-0 sm:block sm:w-[130px]">
                        <div className="text-text-dim font-mono text-[11px] font-medium leading-tight">
                          Block {tx.blockNumber}
                        </div>
                        <div className="text-text-muted font-mono text-[10px] leading-tight">
                          {formatTimestamp(tx.timestamp)}{" "}
                          <span className="opacity-60">
                            {"\u00B7"} {relTime}
                          </span>
                        </div>
                      </div>

                      {/* Status */}
                      <div className="w-5 shrink-0 text-center">
                        {isReverted ? (
                          <span
                            className="text-[11px] text-red-400"
                            title="Reverted"
                          >
                            {"\u2717"}
                          </span>
                        ) : tx.status === "success" ? (
                          <span
                            className="text-[11px] text-green-600/50"
                            title="Success"
                          >
                            {"\u2713"}
                          </span>
                        ) : null}
                      </div>

                      {/* To (always visible) */}
                      <div className="w-[80px] shrink-0 sm:w-[110px]">
                        {tx.to ? (
                          <ResolvedAddressCell
                            address={tx.to}
                            names={mergedNames}
                            onClickAddress={handleAddressClick}
                          />
                        ) : (
                          <span className="font-mono text-xs text-[var(--color-warning)]">
                            CREATE
                          </span>
                        )}
                      </div>

                      {/* Action (flex, takes remaining space) */}
                      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
                        <ProtocolBadge protocol={tx.decoded.protocol} />
                        <span
                          className={cn(
                            "whitespace-nowrap text-xs font-medium",
                            ACTION_CATEGORY_COLORS[
                              classifyAction(tx.decoded.action)
                            ],
                          )}
                        >
                          {tx.decoded.action}
                        </span>
                        {tx.decoded.details && (
                          <span className="text-text-muted truncate text-[11px]">
                            {tx.decoded.details}
                          </span>
                        )}
                        {isReverted && (
                          <Badge variant="error" className="ml-1">
                            REVERTED
                          </Badge>
                        )}
                      </div>

                      {/* Value */}
                      <div
                        className={cn(
                          "hidden shrink-0 text-right font-mono text-xs sm:block sm:w-[80px]",
                          ethValue > 0n
                            ? "text-[var(--color-success)]"
                            : "text-transparent",
                        )}
                      >
                        {ethValue > 0n ? formatEthDisplay(tx.value) : ""}
                      </div>

                      {/* Gas */}
                      <div className="flex w-[70px] shrink-0 items-center justify-end gap-1.5 sm:w-[90px]">
                        {tx.gasUsed !== undefined && (
                          <>
                            <div className="h-1 w-8 overflow-hidden rounded-full bg-[rgba(255,255,255,0.06)]">
                              <div
                                className={cn(
                                  "h-full rounded-full",
                                  gasPercent !== null && gasPercent > 5
                                    ? "bg-red-500/70"
                                    : gasPercent !== null && gasPercent > 1
                                      ? "bg-yellow-500/60"
                                      : "bg-green-500/40",
                                )}
                                style={{
                                  width: `${String(Math.min((gasPercent ?? 0) * 10, 100))}%`,
                                }}
                              />
                            </div>
                            <span className="text-text-muted font-mono text-[10px]">
                              {formatGasCompact(tx.gasUsed)}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded detail panel */}
                  {isExpanded &&
                    (() => {
                      const receipt = expandedReceipts[tx.hash];
                      const decodedLogs = expandedDecodedLogs[tx.hash] ?? [];
                      const expandedGasUsed = receipt
                        ? Number(BigInt(receipt.gasUsed))
                        : null;
                      const expandedGasPercent =
                        expandedGasUsed !== null
                          ? ((expandedGasUsed / gasLimit) * 100).toFixed(2)
                          : null;
                      const expandedGasNum = expandedGasPercent
                        ? Number(expandedGasPercent)
                        : 0;
                      const gasBarColor =
                        expandedGasNum < 50
                          ? "bg-green-500"
                          : expandedGasNum < 80
                            ? "bg-yellow-500"
                            : "bg-red-500";

                      const decodedCall =
                        tx.input !== "0x"
                          ? decodeCalldata(tx.input, mergedNames)
                          : null;

                      return (
                        <div className="border-b border-[var(--glass-border)] bg-[rgba(201,168,76,0.02)]">
                          <div className="space-y-3 px-5 py-4 pl-6">
                            {/* Tx Hash */}
                            <div className="flex items-center gap-2">
                              <span className="text-text-muted text-xs">
                                Tx Hash:
                              </span>
                              <code className="text-gold-dim font-mono text-xs">
                                {tx.hash}
                              </code>
                              <CopyBtn text={tx.hash} />
                            </div>

                            {/* Metadata row */}
                            <div className="flex flex-wrap gap-6 text-xs">
                              <div>
                                <span className="text-text-muted">Block: </span>
                                <span className="text-text-dim font-mono">
                                  {tx.blockNumber}
                                </span>
                              </div>
                              <div>
                                <span className="text-text-muted">Time: </span>
                                <span className="text-text-dim font-mono">
                                  {formatTimestamp(tx.timestamp)}
                                </span>
                              </div>
                              {BigInt(tx.value) !== 0n && (
                                <div>
                                  <span className="text-text-muted">
                                    Value:{" "}
                                  </span>
                                  <span className="font-mono text-[var(--color-success)]">
                                    {formatEthDisplay(tx.value)} ETH
                                  </span>
                                </div>
                              )}
                              {tx.status && (
                                <div>
                                  <span className="text-text-muted">
                                    Status:{" "}
                                  </span>
                                  <span
                                    className={
                                      tx.status === "success"
                                        ? "text-green-400"
                                        : "text-red-400"
                                    }
                                  >
                                    {tx.status === "success"
                                      ? "\u2713 Success"
                                      : "\u2717 Reverted"}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Protocol + Action */}
                            <div className="text-xs">
                              <span className="text-text-muted">
                                Protocol:{" "}
                              </span>
                              <ProtocolBadge protocol={tx.decoded.protocol} />
                              <span className="text-text-dim ml-2">
                                {tx.decoded.action}
                              </span>
                            </div>
                            {tx.decoded.details && (
                              <div className="text-xs">
                                <span className="text-text-muted">
                                  Details:{" "}
                                </span>
                                <span className="text-text-dim">
                                  {tx.decoded.details}
                                </span>
                              </div>
                            )}

                            {/* Gas Usage */}
                            {expandedGasUsed !== null && (
                              <div>
                                <span className="text-text-muted text-xs">
                                  Gas:{" "}
                                </span>
                                <span className="text-text-dim font-mono text-xs">
                                  {expandedGasUsed.toLocaleString()} /{" "}
                                  {gasLimit.toLocaleString()} (
                                  {expandedGasPercent}%)
                                </span>
                                <div className="mt-1 h-2 w-56 overflow-hidden rounded-full bg-[rgba(255,255,255,0.06)]">
                                  <div
                                    className={cn(
                                      "h-full rounded-full",
                                      gasBarColor,
                                    )}
                                    style={{
                                      width: `${String(Math.min(expandedGasNum, 100))}%`,
                                    }}
                                  />
                                </div>
                              </div>
                            )}

                            {/* Decoded Calldata */}
                            {decodedCall && (
                              <div>
                                <div className="text-text-muted mb-1 flex items-center gap-2 text-xs font-semibold">
                                  Decoded Call
                                  <CopyBtn
                                    text={`${decodedCall.functionName}(${Object.entries(
                                      decodedCall.args,
                                    )
                                      .map(([n, v]) => `${n}: ${v}`)
                                      .join(", ")})`}
                                  />
                                </div>
                                <div className="rounded bg-[rgba(0,0,0,0.2)] p-2 font-mono text-[10px]">
                                  <span className="text-amber-400">
                                    {decodedCall.functionName}
                                  </span>
                                  (
                                  {Object.entries(decodedCall.args).map(
                                    ([name, val], ai, arr) => {
                                      const resolvedNames =
                                        Object.values(mergedNames);
                                      const isResolved =
                                        resolvedNames.includes(val);
                                      return (
                                        <span key={name}>
                                          <span className="text-text-muted">
                                            {name}
                                          </span>
                                          :{" "}
                                          <span
                                            className={
                                              isResolved
                                                ? "text-green-400"
                                                : "text-text-dim"
                                            }
                                          >
                                            {val}
                                          </span>
                                          {ai < arr.length - 1 ? ", " : ""}
                                        </span>
                                      );
                                    },
                                  )}
                                  )
                                </div>
                              </div>
                            )}

                            {/* Decoded Events */}
                            {decodedLogs.length > 0 && (
                              <div>
                                <span className="text-text-muted mb-1 block text-xs font-semibold">
                                  Events ({decodedLogs.length})
                                </span>
                                <ul className="space-y-0.5">
                                  {decodedLogs.map((log, li) => {
                                    const logEntry = receipt?.logs[li];
                                    const emittingContract = logEntry?.address
                                      ? (getAccountName(
                                          logEntry.address,
                                          mergedNames,
                                        ) ?? undefined)
                                      : undefined;

                                    const icon =
                                      EVENT_ICONS[log.eventName] ?? "";

                                    return (
                                      <li
                                        key={li}
                                        className="text-text-dim font-mono text-[10px]"
                                      >
                                        {icon && (
                                          <span className="mr-1 opacity-60">
                                            {icon}
                                          </span>
                                        )}
                                        <span
                                          className={eventColorClass(
                                            log.eventName,
                                          )}
                                        >
                                          {log.eventName}
                                        </span>
                                        (
                                        {Object.entries(log.args)
                                          .map(([k, v]) => {
                                            const formatted =
                                              formatLogArgWithContext(
                                                k,
                                                v,
                                                mergedNames,
                                                emittingContract,
                                              );
                                            return `${k}: ${contextualizeZeroAddr(k, formatted)}`;
                                          })
                                          .join(", ")}
                                        )
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            )}

                            {/* Raw Calldata */}
                            {tx.input !== "0x" && (
                              <details>
                                <summary className="text-text-muted cursor-pointer text-xs">
                                  Raw Calldata
                                </summary>
                                <div className="mt-1 flex items-start gap-2">
                                  <pre className="flex-1 overflow-x-auto break-all rounded bg-[rgba(0,0,0,0.3)] p-2 font-mono text-[10px]">
                                    <span className="text-amber-400">
                                      {tx.input.slice(0, 10)}
                                    </span>
                                    {tx.input.length > 10 && (
                                      <span className="text-text-dim">
                                        {tx.input
                                          .slice(10)
                                          .match(/.{1,64}/g)
                                          ?.map((chunk, ci) => (
                                            <Fragment key={ci}>
                                              {"\n"}
                                              {chunk}
                                            </Fragment>
                                          ))}
                                      </span>
                                    )}
                                  </pre>
                                  <CopyBtn text={tx.input} />
                                </div>
                              </details>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                </Fragment>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {transactions.length >= txLimit && (
          <div className="text-text-muted border-t border-[var(--glass-border)] py-3 text-center text-xs">
            Showing last {txLimit} transactions{" "}
            <span className="mx-1 text-white/20">{"\u00B7"}</span>{" "}
            <button
              type="button"
              onClick={() => {
                setTxLimit((prev) => prev + 50);
              }}
              className="text-gold hover:underline"
            >
              Load earlier
            </button>
          </div>
        )}
      </Glass>
    </div>
  );
}
